import { Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { COUNTRIES, roundFlagUrl } from "@/data/countries";
import { ArticleSidebar } from "./Sidebar";

import { ShareBar } from "./ShareBar";
import { KeywordNavBlock } from "./KeywordNavBlock";
import { HubEditorial } from "./HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";

interface Props {
  topic: "Immigration" | "Jobs" | "Finance" | "Tax";
  intro: string;
}

export function TopicPage({ topic, intro }: Props) {
  return (
    <>
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">
            LifeWestWay · Playbooks
          </p>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground sm:text-5xl">
            {topic} across six Western destinations
          </h1>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            This {topic.toLowerCase()} hub compares {topic.toLowerCase()} rules, costs and
            practical steps across six Western destinations — the USA, UK, Australia, Italy,
            Portugal and Canada. {intro}
          </p>

          {/* Editorial standards trust strip */}
          <div className="mt-6 inline-flex flex-wrap items-center gap-2 rounded-full border border-accent/25 bg-accent/5 px-4 py-2 text-xs">
            <span className="font-semibold uppercase tracking-[0.15em] text-accent">
              Our Standards
            </span>
            <span className="text-muted-foreground">
              Every guide is sourced from official government bodies and updated as rules
              change.
            </span>
            <Link title="Read the LifeWestWay mission and vision"
              to="/mission-vision"
              className="font-semibold text-accent hover:underline focus-visible:outline-none focus-visible:underline"
            >
              See our Mission & Vision →
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        
        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="grid gap-5 sm:grid-cols-2">
              {COUNTRIES.map((c) => (
                <Link title={`Open the ${c.name} country guide`}
                  key={c.slug}
                  to={"/$country"}
                  params={{ country: c.slug }}
                  className="group flex items-start gap-4 rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-md"
                >
                  <SafeImage width={64} height={64}
                    src={roundFlagUrl(c.code)}
                    alt={`${c.name} flag`}
                    className="h-11 w-11 shrink-0 rounded-full ring-1 ring-border"
                  />
                  <div className="min-w-0">
                    <h2 className="font-display text-lg font-bold text-foreground group-hover:text-accent">
                      {topic} in {c.name}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">{c.heroBlurb}</p>
                  </div>
                </Link>
              ))}
            </div>

            {COUNTRIES.map((c) => (
              <KeywordNavBlock
                key={`nav-${c.slug}`}
                topic={topic}
                country={c.slug}
                countryLabel={c.name}
                limit={4}
              />
            ))}

            <HubEditorial data={HUB_EDITORIAL[topic.toLowerCase()]} />
          </div>
          <div className="lg:col-span-4">
            <ArticleSidebar />
          </div>
        </div>
      </div>
      <ShareBar title={`${topic} — LifeWestWay`} />
    </>
  );
}
